import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { ExamdojoAgGridModule, processCellForClipboard } from '@examdojo/ag-grid';
import {
  CellClassParams,
  CellClickedEvent,
  CellFocusedEvent,
  CellMouseOverEvent,
  CellValueChangedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ValueGetterParams,
  ValueParserParams,
} from 'ag-grid-community';
import { v4 as uuid } from 'uuid';
import type { GridHighlight } from '../../measures/measures/measures-highlight.util';
import { DataInputCellValue, DataInputColumnType, DataInputConfig, DataInputRow } from '../../../shared/model';
import { AddColumnHeaderComponent, AddColumnHeaderParams } from './add-column-header/add-column-header.component';
import { EditableHeaderComponent, EditableHeaderParams } from './editable-header/editable-header.component';

@Component({
  selector: 'dojo-data-input',
  templateUrl: './data-input.component.html',
  styleUrl: './data-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ExamdojoAgGridModule],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class DataInputComponent {
  private static readonly DEFAULT_COL_WIDTH = 100;
  private static readonly EMPTY_ROW_BUFFER = 10;
  private static readonly ROW_NUMBER_COL_WIDTH = 50;

  private readonly elementRef = inject(ElementRef);
  private gridApi: GridApi<DataInputRow> | null = null;
  private pendingSync = false;
  private suppressNextRowDataUpdate = false;

  readonly config = input.required<DataInputConfig>();
  readonly editable = input(true);
  readonly gridHighlight = input<GridHighlight | null>(null);
  readonly hoveredPointIndex = input<number | null>(null);
  readonly hoveredPointSource = input<'grid' | 'chart' | null>(null);

  readonly configChange = output<DataInputConfig>();
  readonly pointHover = output<{ index: number | null; source: 'grid' | 'chart' }>();

  /** Refresh grid cells and scroll first match into view when highlight changes */
  private readonly highlightEffect = effect(() => {
    const highlight = this.gridHighlight();
    untracked(() => {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.refreshCells({ force: true });

      // Scroll to the first matching row when specific values are highlighted
      if (
        !highlight ||
        highlight.highlightAllColor ||
        (highlight.exactValues.size === 0 && highlight.ranges.length === 0)
      ) {
        return;
      }

      const config = this.config();
      if (config.columns.length === 0) {
        return;
      }

      const colId = config.columns[0].id;
      const rowCount = this.gridApi.getDisplayedRowCount();

      for (let i = 0; i < rowCount; i++) {
        const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
        const value = rowNode?.data?.[colId];
        if (typeof value !== 'number') {
          continue;
        }

        if (highlight.exactValues.has(value) || highlight.ranges.some((r) => value >= r.min && value <= r.max)) {
          this.gridApi.ensureIndexVisible(i, 'middle');
          return;
        }
      }
    });
  });

  /** Highlight and scroll to the hovered point row (only scroll when hover comes from chart) */
  private readonly correlationHighlightEffect = effect(() => {
    const pointIndex = this.hoveredPointIndex();
    const source = this.hoveredPointSource();
    untracked(() => {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.refreshCells({ force: true });
      // eslint-disable-next-line eqeqeq -- intentional nullish check (null | undefined)
      if (pointIndex != null && source === 'chart') {
        this.gridApi.ensureIndexVisible(pointIndex, 'middle');
      }
    });
  });

  readonly focusedColId = signal<string | null>(null);
  readonly focusedRowIndex = signal<number | null>(null);

  readonly columnDefs = computed<Array<ColDef<DataInputRow>>>(() => {
    const config = this.config();
    const isEditable = this.editable();

    const cols: Array<ColDef<DataInputRow>> = [];

    // Row number column
    cols.push({
      colId: '__rowNumber',
      headerName: '',
      width: DataInputComponent.ROW_NUMBER_COL_WIDTH,
      pinned: 'left',
      lockPinned: true,
      editable: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressHeaderMenuButton: true,
      cellClass: 'row-number-cell',
      headerClass: 'row-number-header',
      cellClassRules: {
        'row-number-active': (params: CellClassParams<DataInputRow>) => params.node.rowIndex === this.focusedRowIndex(),
      },
      valueGetter: (params: ValueGetterParams<DataInputRow>) =>
        params.node?.rowIndex !== null && params.node?.rowIndex !== undefined ? params.node.rowIndex + 1 : '',
    });

    // Data columns with named headers
    for (const col of config.columns) {
      const colDef: ColDef<DataInputRow> = {
        colId: col.id,
        headerName: col.header,
        field: col.id,
        editable: isEditable,
        width: col.width ?? DataInputComponent.DEFAULT_COL_WIDTH,
        suppressMovable: true,
        suppressSizeToFit: true,
        headerComponent: EditableHeaderComponent,
        headerComponentParams: {
          editable: isEditable && config.editableHeaders !== false,
          onHeaderRenamed: (colId: string, newName: string) => this.onHeaderRenamed(colId, newName),
          onColumnDeleted: (colId: string) => this.deleteColumn(colId),
          onColumnTypeChanged: (colId: string, newType: DataInputColumnType) =>
            this.onColumnTypeChanged(colId, newType),
          getIsActive: () => this.focusedColId() === col.id,
          getColType: () => this.config().columns.find((c) => c.id === col.id)?.colType ?? 'number',
          onColumnSelected: (colId: string) => this.selectEntireColumn(colId),
        } satisfies EditableHeaderParams,
      };

      colDef.cellStyle = (params: CellClassParams<DataInputRow>) => {
        const noHighlight = { backgroundColor: '' };

        // Correlation point hover highlight (entire row)
        const hoveredPointIdx = this.hoveredPointIndex();
        if (hoveredPointIdx !== null && params.node.rowIndex === hoveredPointIdx) {
          return { backgroundColor: 'rgba(245, 158, 11, 0.15)' }; // orange tint
        }

        const highlight = this.gridHighlight();
        if (!highlight) {
          return noHighlight;
        }
        const value = params.value;
        if (typeof value !== 'number') {
          return noHighlight;
        }
        // Exact values take priority (most specific)
        const exactColor = highlight.exactValues.get(value);
        if (exactColor) {
          return { backgroundColor: exactColor };
        }
        // Then check ranges
        for (const range of highlight.ranges) {
          if (value >= range.min && value <= range.max) {
            return { backgroundColor: range.color };
          }
        }
        // Then highlightAll (least specific)
        if (highlight.highlightAllColor) {
          return { backgroundColor: highlight.highlightAllColor };
        }
        return noHighlight;
      };

      if (col.colType === 'number') {
        colDef.valueParser = (params: ValueParserParams<DataInputRow>) => {
          const value = params.newValue;
          if (value === '' || value === null || value === undefined) {
            return null;
          }
          const parsed = Number(value);
          return isNaN(parsed) ? null : parsed;
        };
      } else if (col.colType === 'boolean') {
        colDef.cellEditor = 'agSelectCellEditor';
        colDef.cellEditorParams = { values: ['True', 'False'] };
        colDef.valueParser = (params: ValueParserParams<DataInputRow>) => {
          const value = params.newValue;
          if (value === '' || value === null || value === undefined) {
            return null;
          }
          const lower = String(value).trim().toLowerCase();
          if (lower === 'true' || lower === 't' || lower === '1' || lower === 'yes') {
            return 'True';
          }
          if (lower === 'false' || lower === 'f' || lower === '0' || lower === 'no') {
            return 'False';
          }
          return null;
        };
      }

      cols.push(colDef);
    }

    // Add column button
    if (isEditable && config.allowAddColumns !== false) {
      cols.push({
        colId: '__addColumn',
        headerName: '',
        field: '__addColumn',
        width: 40,
        editable: false,
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        cellClass: 'add-column-cell',
        headerComponent: AddColumnHeaderComponent,
        headerComponentParams: {
          onAddColumn: () => this.addColumn(),
        } satisfies AddColumnHeaderParams,
      });
    }

    return cols;
  });

  private lastRowData: DataInputRow[] = [];

  readonly rowData = computed<DataInputRow[]>(() => {
    const config = this.config();

    // When the change came from a cell edit, the grid already has the correct data.
    // Skip rebuilding rowData to avoid a full grid reset that causes scroll jumps.
    if (this.suppressNextRowDataUpdate) {
      this.suppressNextRowDataUpdate = false;
      return this.lastRowData;
    }

    const dataRowCount = config.rows.length;
    const totalRows = Math.max(config.rowCount ?? 0, dataRowCount + DataInputComponent.EMPTY_ROW_BUFFER);
    const rows: DataInputRow[] = [];

    for (let i = 0; i < totalRows; i++) {
      const existingRow = config.rows[i];
      rows.push(existingRow ? { ...existingRow } : {});
    }

    this.lastRowData = rows;
    return rows;
  });

  readonly gridOptions = computed<GridOptions<DataInputRow>>(() => {
    const isEditable = this.editable();

    return {
      singleClickEdit: false,
      suppressClickEdit: !isEditable,
      undoRedoCellEditing: isEditable,
      undoRedoCellEditingLimit: 50,
      stopEditingWhenCellsLoseFocus: true,
      suppressRowClickSelection: true,
      suppressRowHoverHighlight: true,
      enableRangeSelection: true,
      enableFillHandle: isEditable,
      processCellForClipboard,
      processCellFromClipboard: (params) => this.processCellFromClipboard(params),
      getContextMenuItems: () => [
        'copy',
        'copyWithHeaders',
        'separator',
        'export',
        ...(isEditable
          ? [
              'separator' as const,
              {
                name: 'Clear All Data',
                icon: '<span class="ag-icon ag-icon-cancel"></span>',
                action: () => this.clearAllData(),
              },
            ]
          : []),
      ],
      headerHeight: 28,
      rowHeight: 28,
    };
  });

  onDocumentClick(event: MouseEvent): void {
    if (!this.gridApi || !this.elementRef.nativeElement) {
      return;
    }

    const target = event.target as HTMLElement;
    const clickedInside = this.elementRef.nativeElement.contains(target);

    if (!clickedInside) {
      this.clearSelection();
      return;
    }

    // Clear selection when clicking on empty space inside the grid (not on a cell or header)
    const clickedOnCell = target.closest('.ag-cell');
    const clickedOnHeader = target.closest('.ag-header-cell');
    if (!clickedOnCell && !clickedOnHeader) {
      this.clearSelection();
    }
  }

  onGridReady(event: GridReadyEvent<DataInputRow>): void {
    this.gridApi = event.api;
  }

  onCellFocused(event: CellFocusedEvent<DataInputRow>): void {
    const column = event.column;
    const colId = column && typeof column !== 'string' ? column.getColId() : null;
    const rowIndex = event.rowIndex ?? null;

    this.focusedColId.set(colId);
    this.focusedRowIndex.set(rowIndex);

    this.gridApi?.refreshHeader();
    this.gridApi?.refreshCells({ columns: ['__rowNumber'], force: true });
  }

  onCellMouseOver(event: CellMouseOverEvent<DataInputRow>): void {
    if (event.rowIndex === null) {
      return;
    }
    // Only set hover if this row has actual data (both x and y)
    const config = this.config();
    const numericCols = config.columns.filter((c) => c.colType === 'number');
    if (numericCols.length < 2) {
      return;
    }
    const row = event.data;
    if (!row) {
      return;
    }
    const x = row[numericCols[0].id];
    const y = row[numericCols[1].id];
    if (typeof x === 'number' && typeof y === 'number') {
      this.pointHover.emit({ index: event.rowIndex, source: 'grid' });
    }
  }

  onCellMouseOut(): void {
    this.pointHover.emit({ index: null, source: 'grid' });
  }

  onCellClicked(event: CellClickedEvent<DataInputRow>): void {
    const colId = event.column.getColId();
    if (colId === '__rowNumber' && event.rowIndex !== null && event.rowIndex !== undefined) {
      this.selectEntireRow(event.api, event.rowIndex);
    }
  }

  onCellValueChanged(event: CellValueChangedEvent<DataInputRow>): void {
    const colId = event.column.getColId();
    if (colId === '__rowNumber' || colId === '__addColumn') {
      return;
    }

    // Don't round-trip through the parent signal — AG Grid already holds the updated data.
    // Sync the grid state outward only when editing stops, to avoid scroll resets.
    this.pendingSync = true;
  }

  onCellEditingStopped(): void {
    if (!this.pendingSync || !this.gridApi) {
      return;
    }
    this.pendingSync = false;

    const updatedRows = this.buildRowsFromGrid(this.config());
    this.suppressNextRowDataUpdate = true;
    this.emitConfigChange({ rows: updatedRows });
  }

  private selectEntireRow(api: GridApi<DataInputRow>, rowIndex: number): void {
    const dataColumns = this.config().columns.map((col) => col.id);
    if (dataColumns.length === 0) {
      return;
    }
    api.clearRangeSelection();
    api.addCellRange({
      rowStartIndex: rowIndex,
      rowEndIndex: rowIndex,
      columns: dataColumns,
    });
  }

  private selectEntireColumn(colId: string): void {
    if (!this.gridApi) {
      return;
    }
    const rowCount = this.gridApi.getDisplayedRowCount();
    this.gridApi.clearRangeSelection();
    this.gridApi.addCellRange({
      rowStartIndex: 0,
      rowEndIndex: rowCount - 1,
      columns: [colId],
    });
  }

  private buildRowsFromGrid(config: DataInputConfig): DataInputRow[] {
    if (!this.gridApi) {
      return config.rows;
    }

    const displayedRowCount = this.gridApi.getDisplayedRowCount();
    const updatedRows: DataInputRow[] = [];
    let lastDataIndex = -1;

    for (let i = 0; i < displayedRowCount; i++) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
      if (!rowNode?.data) {
        updatedRows.push({});
        continue;
      }

      const row: DataInputRow = {};
      let hasData = false;

      for (const col of config.columns) {
        const value = rowNode.data[col.id] as DataInputCellValue;
        if (value !== null && value !== undefined && value !== '') {
          row[col.id] = value;
          hasData = true;
        } else {
          row[col.id] = null;
        }
      }

      updatedRows.push(row);
      if (hasData) {
        lastDataIndex = i;
      }
    }

    // Trim trailing empty rows, keeping only rows up to the last one with data
    return updatedRows.slice(0, lastDataIndex + 1);
  }

  private onHeaderRenamed(colId: string, newName: string): void {
    const updatedColumns = this.config().columns.map((col) => (col.id === colId ? { ...col, header: newName } : col));
    this.emitConfigChange({ columns: updatedColumns });
  }

  private onColumnTypeChanged(colId: string, newType: DataInputColumnType): void {
    const config = this.config();
    const column = config.columns.find((c) => c.id === colId);
    if (!column || column.colType === newType) {
      return;
    }

    const oldType = column.colType;
    const isSafe = this.isSafeTypeConversion(oldType, newType);

    if (!isSafe) {
      const confirmed = confirm(
        `Changing this column from "${this.typeDisplayName(oldType)}" to "${this.typeDisplayName(
          newType,
        )}" will clear all values in this column.\n\nDo you want to continue?`,
      );
      if (!confirmed) {
        return;
      }
    }

    const updatedColumns = config.columns.map((col) => (col.id === colId ? { ...col, colType: newType } : col));
    const updatedRows = isSafe
      ? this.convertColumnValues(colId, oldType, newType, config.rows)
      : this.clearColumnValues(colId, config.rows);

    this.emitConfigChange({ columns: updatedColumns, rows: updatedRows });
  }

  private isSafeTypeConversion(from: DataInputColumnType, to: DataInputColumnType): boolean {
    return (from === 'number' && to === 'text') || (from === 'boolean' && to === 'text');
  }

  private convertColumnValues(
    colId: string,
    fromType: DataInputColumnType,
    _toType: DataInputColumnType,
    rows: DataInputRow[],
  ): DataInputRow[] {
    if (fromType === 'number') {
      // number → text: convert numbers to their string representation
      return rows.map((row) => {
        const val = row[colId];
        if (typeof val === 'number') {
          return { ...row, [colId]: String(val) };
        }
        return row;
      });
    }
    // boolean → text: "True"/"False" are already strings, no conversion needed
    return rows;
  }

  private clearColumnValues(colId: string, rows: DataInputRow[]): DataInputRow[] {
    return rows.map((row) => ({ ...row, [colId]: null }));
  }

  private typeDisplayName(type: DataInputColumnType): string {
    switch (type) {
      case 'number':
        return 'Number';
      case 'text':
        return 'Text';
      case 'boolean':
        return 'True / False';
    }
  }

  private deleteColumn(colId: string): void {
    const updatedColumns = this.config().columns.filter((col) => col.id !== colId);
    this.emitConfigChange({ columns: updatedColumns });
  }

  private addColumn(): void {
    const config = this.config();
    const newColumn = {
      id: uuid(),
      header: '',
      colType: 'number' as const,
    };
    this.emitConfigChange({ columns: [...config.columns, newColumn] });
  }

  private processCellFromClipboard(params: { value: string; column?: { getColId(): string } }): DataInputCellValue {
    const value = params.value;
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const colId = params.column?.getColId();
    const colDef = this.config().columns.find((c) => c.id === colId);
    const colType = colDef?.colType ?? 'text';

    if (colType === 'number') {
      const parsed = Number(value);
      return isNaN(parsed) ? null : parsed;
    }

    if (colType === 'boolean') {
      const lower = String(value).trim().toLowerCase();
      if (lower === 'true' || lower === 't' || lower === '1' || lower === 'yes') {
        return 'True';
      }
      if (lower === 'false' || lower === 'f' || lower === '0' || lower === 'no') {
        return 'False';
      }
      return null;
    }

    return value;
  }

  private clearAllData(): void {
    this.emitConfigChange({ rows: [], rowCount: 0 });
  }

  private clearSelection(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.clearRangeSelection();
    this.gridApi.clearFocusedCell();
    this.focusedColId.set(null);
    this.focusedRowIndex.set(null);
    this.gridApi.refreshHeader();
    this.gridApi.refreshCells({ columns: ['__rowNumber'], force: true });
  }

  private emitConfigChange(partial: Partial<DataInputConfig>): void {
    this.configChange.emit({ ...this.config(), ...partial });
  }
}
